import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Github, ExternalLink, Layers3 } from "lucide-react";

const projects = [
  {
    number: "01",
    title: "GanpatiTrust Portal",
    category: "Full Stack Application",
    description:
      "A modern digital portal for Shree Maha Ganpati Sansthan, Rajur, designed to provide information, services and a better digital experience for devotees.",
    tech: ["React", "Spring Boot", "MySQL", "Tailwind CSS"],
    accent: "orange",
  },
  {
    number: "02",
    title: "Zomato Clone",
    category: "Food Ordering Platform",
    description:
      "A role-based food ordering platform with separate experiences for customers, administrators and restaurant owners, including restaurant and order management.",
    tech: ["React", "Spring Boot", "JWT", "MySQL"],
    accent: "orange",
  },
  {
    number: "03",
    title: "Music Player App",
    category: "Frontend Application",
    description:
      "A responsive music application featuring authentication, search, audio controls, playlist interactions and a modern user interface.",
    tech: ["React", "CSS", "Audio API"],
    accent: "orange",
  },
  {
    number: "04",
    title: "E-Commerce Backend API",
    category: "Backend Engineering",
    description:
      "A scalable Spring Boot backend architecture covering product management, authentication, order processing and database persistence.",
    tech: ["Java", "Spring Boot", "MySQL", "JWT"],
    accent: "orange",
  },
  {
    number: "05",
    title: "Portfolio Website",
    category: "Personal Brand",
    description:
      "A responsive developer portfolio focused on presenting technical expertise, projects, professional experience and contact information.",
    tech: ["React", "Tailwind CSS", "Framer Motion"],
    accent: "orange",
  },
  {
    number: "06",
    title: "Blog Platform",
    category: "REST API",
    description:
      "A secure blogging platform supporting user registration, content creation, comments, likes and RESTful backend operations.",
    tech: ["Java", "Spring Boot", "MySQL", "JWT"],
    accent: "orange",
  },
];

const Projects = () => {
  return (
    <section
      id="projects"
      className="relative overflow-hidden bg-[#07070a] px-6 py-28 text-white sm:px-10 lg:px-20"
    >
      <div className="absolute right-0 top-40 h-96 w-96 rounded-full bg-orange-500/[0.035] blur-[120px]" />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16 flex flex-col justify-between gap-6 lg:flex-row lg:items-end"
        >
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-orange-400">
              Selected Work
            </p>

            <h2 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
              Things I've <span className="text-orange-500">built.</span>
            </h2>
          </div>

          <p className="max-w-md text-base leading-7 text-gray-500 lg:text-right">
            A selection of applications and backend systems built with a focus
            on scalability, security, maintainability and user experience.
          </p>
        </motion.div>

        {/* Projects */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project, index) => (
            <motion.article
              key={project.title}
              initial={{
                opacity: 0,
                y: 35,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
                margin: "-70px",
              }}
              transition={{
                duration: 0.6,
                delay: index * 0.07,
              }}
              whileHover={{
                y: -7,
              }}
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.025] p-6 transition-all duration-500 hover:border-orange-500/25 hover:bg-white/[0.04]"
            >
              {/* Hover glow */}
              <div className="pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full bg-orange-500/10 opacity-0 blur-[70px] transition duration-500 group-hover:opacity-100" />

              {/* Number */}
              <div className="flex items-start justify-between">
                <span className="font-mono text-xs text-gray-700">
                  /{project.number}
                </span>

                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gray-500 transition group-hover:border-orange-500/20 group-hover:bg-orange-500/10 group-hover:text-orange-400">
                  <ArrowUpRight size={18} />
                </div>
              </div>

              {/* Category */}
              <div className="mt-10 flex items-center gap-2">
                <Layers3 size={14} className="text-orange-400" />

                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-orange-400">
                  {project.category}
                </span>
              </div>

              <h3 className="mt-3 text-2xl font-bold tracking-tight text-white">
                {project.title}
              </h3>

              <p className="mt-4 min-h-[120px] text-sm leading-7 text-gray-500">
                {project.description}
              </p>

              {/* Tech */}
              <div className="mt-6 flex flex-wrap gap-2">
                {project.tech.map((tech) => (
                  <span
                    key={tech}
                    className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-1.5 text-[10px] font-medium text-gray-400 transition group-hover:border-orange-500/10 group-hover:text-gray-300"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-7 flex items-center justify-between border-t border-white/5 pt-5">
                <span className="text-xs text-gray-700">Case Study</span>

                <div className="flex items-center gap-3 text-gray-600">
                  <Github size={16} />

                  <ExternalLink size={16} />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
